import {
  buildReportImagesZipFilename,
  dedupeReportImageFilename,
  ensureFlatZipEntryFilename,
  sanitizeReportImageEntryName,
} from './report-images-zip-filename.js';

export interface GalleryDownloadFilterContext {
  retailerId: string;
  cycleId: string;
  campaignId: string;
  retailerName?: string | null;
  cycleName?: string | null;
  campaignName?: string | null;
  taskType?: string;
  reviewStatus?: string;
}

export interface GalleryDownloadImageMeta {
  id: number;
  storeName: string;
  campaignName: string;
}

export type GalleryDownloadNamingMode = 'campaign_scoped' | 'cycle_scoped' | 'mixed';

function toFilenamePart(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export function resolveGalleryDownloadNamingMode(
  context: GalleryDownloadFilterContext,
): GalleryDownloadNamingMode {
  if (context.campaignId !== 'all' && context.cycleId !== 'all') {
    return 'campaign_scoped';
  }
  if (context.cycleId !== 'all') {
    return 'cycle_scoped';
  }
  return 'mixed';
}

function buildGalleryDownloadZipSuffix(context: GalleryDownloadFilterContext): string {
  const parts: string[] = [];
  if (context.taskType === 'POSTING') parts.push('Posting');
  if (context.taskType === 'REMOVAL') parts.push('Removal');
  if (context.reviewStatus === 'retailer_ready') parts.push('RetailerReady');
  if (context.reviewStatus === 'approved') parts.push('Approved');
  if (context.reviewStatus === 'rejected') parts.push('Rejected');
  return parts.length > 0 ? `_${parts.join('_')}` : '';
}

export function buildGalleryDownloadZipFilename(
  context: GalleryDownloadFilterContext,
  downloadedAt: Date = new Date(),
): string {
  const mode = resolveGalleryDownloadNamingMode(context);
  const suffix = buildGalleryDownloadZipSuffix(context);

  if (mode === 'campaign_scoped' && context.campaignName) {
    try {
      const base = buildReportImagesZipFilename(context.campaignName, context.cycleName);
      return base.replace(/\.zip$/i, `${suffix}.zip`);
    } catch {
      // Fall through to generic naming when campaign metadata is incomplete.
    }
  }

  if (mode === 'cycle_scoped') {
    const retailer = toFilenamePart(context.retailerName);
    const cycle = toFilenamePart(context.cycleName);
    if (retailer && cycle) {
      return `${retailer}_${cycle}${suffix}.zip`;
    }
    if (cycle) {
      return `${cycle}${suffix}.zip`;
    }
  }

  const stamp = downloadedAt.toISOString().slice(0, 16).replace('T', '-').replace(':', '');
  return `gallery_${stamp}${suffix}.zip`;
}

export function buildGalleryDownloadImageBasename(
  image: GalleryDownloadImageMeta,
  mode: GalleryDownloadNamingMode,
): string {
  const store = sanitizeReportImageEntryName(image.storeName) || `image-${image.id}`;
  const campaign = sanitizeReportImageEntryName(image.campaignName) || 'Campaign';

  switch (mode) {
    case 'campaign_scoped':
    case 'cycle_scoped':
      return `${campaign}_${store}`;
    case 'mixed':
      return `${store}_${campaign}`;
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}

export function buildGalleryDownloadEntryFilename(
  usedNames: Map<string, number>,
  image: GalleryDownloadImageMeta,
  mode: GalleryDownloadNamingMode,
): string {
  const baseName = buildGalleryDownloadImageBasename(image, mode);
  return ensureFlatZipEntryFilename(dedupeReportImageFilename(usedNames, baseName));
}
